import { FC, Fragment, useState } from 'react';
import classNames from 'classnames';

import { Menu, Transition } from '@headlessui/react';
import {
  CogIcon,
  TrashIcon,
  CollectionIcon,
  PlusSmIcon,
  XIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/outline';

import {
  ProviderCollection,
  ProviderCollections,
} from '../../../types/providerCollection';
import { ContextMenuPosition } from '../../../types/contextMenu';

import MenuButton from '../../../components/MenuButton';
import {
  MENU_OFFSET_Y_PROPORTION,
  MENU_OFFSET_X_PROPORTION,
} from '../../../lib/window';
import { defaultMenuTransitionProps } from '../../../lib/animation';

import PencilSquare from '../../../svgs/PencilSquare';
import { isStandardAssessment } from '../../../lib/assessments';
import { ActivityListDataFragment } from '../../../../generated/graphql';
import { isProviderActivityPDF } from '../../../types/activity';

const MENU_ROW_COUNT = 3;
const HEIGHT_PADDING = 10;
const MENU_WIDTH_PIXELS = 256;

type ActivityActionsMenuProps = {
  isOpen?: boolean;
  setIsOpen?: React.MouseEventHandler<HTMLButtonElement>;
  position: ContextMenuPosition;
  providerActivity?: ActivityListDataFragment;
  providerCollections: ProviderCollections | undefined;
  selectedProviderCollection: ProviderCollection | undefined;
  addActivityToProviderCollection: (
    providerCollection: ProviderCollection,
    providerActivity: ActivityListDataFragment,
  ) => Promise<void>;
  onClickRemoveActivityFromProviderCollection?: React.MouseEventHandler<HTMLButtonElement>;
  onClickAddActivityToProviderCollection?: React.MouseEventHandler<HTMLButtonElement>;
  onClickArchiveResource?: React.MouseEventHandler<HTMLButtonElement>;
  onClickDuplicateResource?: React.MouseEventHandler<HTMLButtonElement>;
  onClickRenameResource?: React.MouseEventHandler<HTMLButtonElement>;
  onClickModifyFileSettings?: React.MouseEventHandler<HTMLButtonElement>;
  setSelectedProviderActivity: React.Dispatch<
    React.SetStateAction<ActivityListDataFragment | undefined>
  >;
  setShowActivityActionsMenu: React.Dispatch<React.SetStateAction<boolean>>;
};

const ActivityActionsMenu: FC<ActivityActionsMenuProps> = ({
  isOpen,
  setIsOpen,
  providerActivity,
  providerCollections,
  selectedProviderCollection,
  addActivityToProviderCollection,
  onClickRemoveActivityFromProviderCollection,
  onClickAddActivityToProviderCollection,
  onClickArchiveResource,
  onClickDuplicateResource,
  onClickRenameResource,
  onClickModifyFileSettings,
  position,
  setShowActivityActionsMenu,
}) => {
  const screenHeight = document.body.scrollHeight;
  const screenWidth = document.body.scrollWidth;

  const menuPositionXRight = Boolean(
    screenWidth * MENU_OFFSET_X_PROPORTION < position.x,
  );
  const menuPositionYAbove = Boolean(
    screenHeight * MENU_OFFSET_Y_PROPORTION < position.y,
  );

  const positionXAdjusted = position.x + HEIGHT_PADDING;

  const providerCollectionCount = providerCollections?.length ?? 0;
  const threeOrLessCollectionOptions = Boolean(
    providerCollectionCount - 1 < MENU_ROW_COUNT, // -1 bc `selectedCollection` and collections resource is already in are filtered from list
  );

  const isAssessment = isStandardAssessment(providerActivity);
  const isFile = isProviderActivityPDF(providerActivity);

  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

  const filteredProviderCollections = providerCollections?.filter(
    (providerCollection) => {
      if (providerCollection === selectedProviderCollection) return false;
      if (
        providerActivity?.providerCollectionIds?.includes(providerCollection.id)
      )
        return false;
      return true;
    },
  );

  return (
    <div className="z-30 flex">
      <Menu as="div" className="inline-block text-left">
        <Transition show={isOpen} as={Fragment} {...defaultMenuTransitionProps}>
          <Menu.Items
            className={classNames(
              'absolute right-0 z-30 mt-2 w-64 origin-top-right divide-y divide-neutral-50 rounded-md bg-white shadow-md focus:outline-none',
            )}
            style={{
              left: menuPositionXRight
                ? positionXAdjusted - MENU_WIDTH_PIXELS
                : positionXAdjusted,
              ...(menuPositionYAbove
                ? {
                    bottom: screenHeight - position.y,
                  }
                : {
                    top: position.y + HEIGHT_PADDING,
                  }),
            }}
            static
          >
            {!isAssessment && (
              <Menu as={Fragment}>
                <Menu.Button as={Fragment}>
                  <button
                    className={classNames(
                      'flex w-full flex-row items-center justify-between px-3 py-4 text-caption hover:bg-secondary-20',
                      'rounded-t-md',
                    )}
                    onMouseEnter={() => setIsSubMenuOpen(true)}
                    onMouseLeave={() => setIsSubMenuOpen(false)}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    Move to collection
                  </button>
                </Menu.Button>
                <Transition
                  show={isSubMenuOpen}
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items
                    className={classNames(
                      'absolute z-30 w-80 rounded-md border border-neutral-50 bg-white shadow-md focus:outline-none',
                      menuPositionXRight ? 'right-[254px]' : '-right-[254px]',
                      !menuPositionYAbove && '-top-[1px]',
                      menuPositionYAbove &&
                        !selectedProviderCollection &&
                        'bottom-0',
                      menuPositionYAbove &&
                        selectedProviderCollection &&
                        threeOrLessCollectionOptions &&
                        '-top-[1px]',
                      menuPositionYAbove &&
                        selectedProviderCollection &&
                        !threeOrLessCollectionOptions &&
                        'bottom-0',
                    )}
                    static
                    onMouseEnter={() => setIsSubMenuOpen(true)}
                    onMouseLeave={() => setIsSubMenuOpen(false)}
                  >
                    <MenuButton
                      titleText="Move to new collection"
                      className={classNames(
                        'border-b border-neutral-50 text-neutral-150',
                        Boolean(filteredProviderCollections?.length)
                          ? 'rounded-t-md'
                          : 'rounded-md',
                      )}
                      onClick={(e) => {
                        onClickAddActivityToProviderCollection?.(e);
                        setShowActivityActionsMenu(false);
                        setIsSubMenuOpen(false);
                        e.stopPropagation();
                      }}
                      iconClassName="text-secondary-125 h-[16px] w-[16px]"
                      IconComponent={PlusSmIcon}
                    />
                    <div className="max-h-72 overflow-y-auto">
                      {filteredProviderCollections?.map(
                        (providerCollection, index) => {
                          const lastProviderCollection =
                            (filteredProviderCollections?.length ?? 0) ===
                            index + 1;

                          return (
                            <MenuButton
                              key={`${providerCollection.name}_${index}`}
                              titleText={providerCollection.name}
                              textClassName="font-medium text-neutral-150"
                              iconClassName="text-secondary-125 h-[16px] w-[16px]"
                              IconComponent={CollectionIcon}
                              iconPosition="left"
                              multiline
                              className={classNames(
                                lastProviderCollection && 'rounded-b-md',
                              )}
                              onClick={async (e) => {
                                e.stopPropagation();
                                setIsOpen?.(e);
                                setIsSubMenuOpen(false);
                                if (providerActivity)
                                  await addActivityToProviderCollection(
                                    providerCollection,
                                    providerActivity,
                                  );
                              }}
                            />
                          );
                        },
                      )}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            )}
            {!isAssessment &&
              (selectedProviderCollection ||
                (providerActivity?.providerCollectionIds?.length ?? 0) > 0) && (
                <MenuButton
                  titleText={
                    selectedProviderCollection
                      ? 'Remove from this collection'
                      : 'Remove from collection'
                  }
                  className="text-neutral-150"
                  iconClassName="text-secondary-125 h-[16px] w-[16px]"
                  IconComponent={XIcon}
                  onClick={(e) => {
                    onClickRemoveActivityFromProviderCollection?.(e);
                    e.stopPropagation();
                    setShowActivityActionsMenu(false);
                  }}
                />
              )}
            {!isAssessment && (
              <>
                <MenuButton
                  titleText="Duplicate"
                  iconClassName="text-secondary-125 h-[12px] w-[12px]"
                  IconComponent={DocumentDuplicateIcon}
                  onClick={(e) => {
                    onClickDuplicateResource?.(e);
                    setShowActivityActionsMenu(false);
                    e.stopPropagation();
                  }}
                />
                <MenuButton
                  titleText="Rename"
                  iconClassName="text-secondary-125 h-[12px] w-[12px]"
                  IconComponent={PencilSquare}
                  onClick={(e) => {
                    onClickRenameResource?.(e);
                    setShowActivityActionsMenu(false);
                    e.stopPropagation();
                  }}
                />
                {isFile && (
                  <MenuButton
                    titleText="Modify file settings"
                    iconClassName="text-secondary-125 h-[12px] w-[12px]"
                    IconComponent={CogIcon}
                    onClick={(e) => {
                      onClickModifyFileSettings?.(e);
                      setShowActivityActionsMenu(false);
                      e.stopPropagation();
                    }}
                  />
                )}
                <MenuButton
                  titleText="Delete"
                  iconClassName="!text-red-100 h-[12px] w-[12px]"
                  className="!rounded-b-md !text-red-100"
                  activeText="!text-red-100"
                  IconComponent={TrashIcon}
                  onClick={(e) => {
                    onClickArchiveResource?.(e);
                    setShowActivityActionsMenu(false);
                    e.stopPropagation();
                  }}
                />
              </>
            )}
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
};

export default ActivityActionsMenu;
